import React, { useState, useEffect } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Box, Button, TextField, MenuItem, Select, FormControl, InputLabel,
  Grid, Pagination, InputAdornment, IconButton, ToggleButton, ToggleButtonGroup
} from "@mui/material";
import { Add, Search, FilterList, Clear, Campaign as CampaignIcon, ViewList, ViewModule } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useCampaignList, useCampaignActions } from "./hooks/useCampaign";
import { StatsGrid, CampaignCard, CampaignListItem, Loading, EmptyState } from "./components/CampaignComponents";
import "./Campaign.css";

const CampaignList = () => {
  const navigate = useNavigate();
  const { campaigns, loading, pagination, fetch } = useCampaignList();
  const { execute, remove } = useCampaignActions(() => fetch(filters));

  const [filters, setFilters] = useState({ page: 1, status: "", type: "", search: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // "list" or "grid"

  useEffect(() => { fetch(filters); }, [filters.page, filters.status, filters.type]);

  const handleSearch = () => fetch({ ...filters, page: 1 });
  const clearFilters = () => setFilters({ page: 1, status: "", type: "", search: "" });
  const hasFilters = filters.status || filters.type || filters.search;

  const stats = {
    total: pagination.total,
    running: campaigns.filter(c => c.status === "running").length,
    completed: campaigns.filter(c => c.status === "completed").length,
    draft: campaigns.filter(c => c.status === "draft").length
  };

  return (
    <Box className="campaign-container">
      <ToastContainer position="top-right" autoClose={3000} />
      {/* Header */}
      <Box className="campaign-header">
        <Box>
          <h1 className="campaign-title">Campaigns</h1>
          <p className="campaign-subtitle">Manage and track your marketing campaigns</p>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate("/campaign/create")}>
          Create Campaign
        </Button>
      </Box>

      {/* Stats */}
      <StatsGrid stats={stats} />

      {/* Search & Filters */}
      <Box className="filters-card">
        <TextField
          placeholder="Search campaigns..."
          value={filters.search}
          onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          fullWidth
          size="small"
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
            endAdornment: filters.search && (
              <IconButton size="small" onClick={() => { setFilters(f => ({ ...f, search: "" })); handleSearch(); }}>
                <Clear fontSize="small" />
              </IconButton>
            )
          }}
        />

        <Box className="filters-row" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" startIcon={<FilterList />} onClick={() => setShowFilters(!showFilters)}>
              Filters {hasFilters && `(${[filters.status, filters.type].filter(Boolean).length})`}
            </Button>
            {hasFilters && <Button startIcon={<Clear />} onClick={clearFilters}>Clear</Button>}
          </Box>
          <ToggleButtonGroup value={viewMode} exclusive onChange={(e, v) => v && setViewMode(v)} size="small">
            <ToggleButton value="list"><ViewList /></ToggleButton>
            <ToggleButton value="grid"><ViewModule /></ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {showFilters && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={filters.status} label="Status" onChange={(e) => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
                  <MenuItem value="">All</MenuItem>
                  {["draft", "scheduled", "running", "paused", "completed", "failed"].map(s => (
                    <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={filters.type} label="Type" onChange={(e) => setFilters(f => ({ ...f, type: e.target.value, page: 1 }))}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="push">Push</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Content */}
      {loading ? <Loading text="Loading campaigns..." /> : campaigns.length === 0 ? (
        <EmptyState
          icon={CampaignIcon}
          title="No campaigns found"
          subtitle={hasFilters ? "Try adjusting your filters" : "Get started by creating your first campaign"}
          action={hasFilters
            ? <Button variant="outlined" onClick={clearFilters}>Clear Filters</Button>
            : <Button variant="contained" startIcon={<Add />} onClick={() => navigate("/campaign/create")}>Create Campaign</Button>
          }
        />
      ) : (
        <>
          {viewMode === "list" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {campaigns.map(c => (
                <CampaignListItem
                  key={c._id}
                  campaign={c}
                  onView={(id) => navigate(`/campaign/${id}`)}
                  onEdit={(id) => navigate(`/campaign/edit/${id}`)}
                  onExecute={execute}
                  onDelete={remove}
                />
              ))}
            </Box>
          ) : (
            <Grid container spacing={3}>
              {campaigns.map(c => (
                <Grid item xs={12} sm={6} md={4} key={c._id}>
                  <CampaignCard
                    campaign={c}
                    onView={(id) => navigate(`/campaign/${id}`)}
                    onEdit={(id) => navigate(`/campaign/edit/${id}`)}
                    onExecute={execute}
                    onDelete={remove}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {pagination.totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={pagination.totalPages}
                page={filters.page}
                onChange={(_, v) => setFilters(f => ({ ...f, page: v }))}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CampaignList;
